import { Component, type ErrorInfo, type ReactNode } from 'react'
import i18n from '@/i18n'
import { reportBoundaryError } from '@/lib/errorReporter'
import { isStaleChunkError, reloadOnceForNewDeploy } from '@/lib/staleDeploy'

interface Props { children: ReactNode }
interface State { error: Error | null; stack: string | null }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, stack: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isStaleChunkError(error) && reloadOnceForNewDeploy()) return
    console.error('[ErrorBoundary]', error, info.componentStack)
    this.setState({ stack: info.componentStack ?? null })
    reportBoundaryError(error, { componentStack: info.componentStack ?? '' })
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="max-w-xl w-full bg-white rounded-xl border border-red-200 shadow-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{i18n.t('hardcoded.errTitle')}</h2>
                <p className="text-sm text-gray-500">{i18n.t('hardcoded.errBody')}</p>
              </div>
            </div>

            <details className="text-xs">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none">
                {i18n.t('hardcoded.errDetails')}
              </summary>
              <div className="mt-2 bg-red-50 rounded-lg p-3 font-mono text-xs text-red-700 break-all whitespace-pre-wrap max-h-32 overflow-y-auto">
                {this.state.error.message}
              </div>
              {this.state.stack && (
                <pre className="mt-2 bg-gray-50 rounded p-2 overflow-x-auto text-gray-500 max-h-40 overflow-y-auto text-[10px] leading-relaxed">
                  {this.state.stack}
                </pre>
              )}
            </details>

            <div className="flex gap-3">
              <button
                onClick={() => this.setState({ error: null, stack: null })}
                className="flex-1 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
              >
                {i18n.t('hardcoded.errRetry')}
              </button>
              <button
                onClick={() => { window.location.href = '/app/dashboard' }}
                className="flex-1 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-sm font-medium text-white transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
